import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ThemeProvider } from '@/lib/theme'
import { toLocale, useI18n } from '@/lib/i18n'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import { useDocumentMeta } from '@/lib/useDocumentMeta'
import { CONTACT_FORM_ENABLED } from '@/lib/features'
import { PAGES } from '@/lib/seo'
import '@/styles/imprint.css'

/**
 * Datenschutzerklärung (GDPR Art. 13 / § 165 TKG 2021). Describes what the
 * site actually does — keep it in step with the code: hosting, the
 * click-to-load map (MapEmbed), browser storage (theme, StorageNotice,
 * sandbox sessions, survey answers), the contact form (only while
 * CONTACT_FORM_ENABLED), surveys and user tests under /archive (generic on
 * purpose: studies come and go, each one shows its own consent text).
 */

type Section = { heading: string; body: ReactNode }

const ext = (href: string, label = href.replace(/^https:\/\//, '')) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
)

const GOOGLE_PRIVACY = 'https://policies.google.com/privacy'
const EMAILJS_PRIVACY = 'https://www.emailjs.com/legal/privacy-policy/'

function copy(to: (p: string) => string) {
  const imprint = (label: string) => <Link to={to('/imprint')}>{label}</Link>
  const studies = (label: string) => <Link to={to('/archive')}>{label}</Link>

  const de: { eyebrow: string; title: string; updated: string; sections: Section[] } = {
    eyebrow: 'Rechtliches',
    title: 'Datenschutzerklärung',
    updated: 'Stand: November 2026',
    sections: [
      {
        heading: 'Verantwortlicher',
        body: (
          <>
            <p>
              SoftMaze Solutions GmbH, Gablenzgasse 21/GL/2-4, 1150 Wien, Österreich
              <br />
              E-Mail: <a href="mailto:info@softmaze.at">info@softmaze.at</a> · Telefon: +43 660 5369991
            </p>
            <p>Weitere Angaben zum Unternehmen finden Sie im {imprint('Impressum')}.</p>
          </>
        ),
      },
      {
        heading: 'Kurz gesagt',
        body: (
          <ul>
            <li>Kein Tracking, keine Analyse-Tools, keine Werbung, keine Social-Media-Plugins.</li>
            <li>Wir setzen keine Cookies.</li>
            <li>Google Maps wird erst geladen, wenn Sie auf „Karte laden“ klicken.</li>
            <li>An Umfragen und Nutzertests nehmen Sie nur mit Ihrer Einwilligung teil.</li>
            <li>Schriften und alle übrigen Dateien der Website kommen von unserem eigenen Hosting.</li>
          </ul>
        ),
      },
      {
        heading: 'Hosting und Server-Protokolle',
        body: (
          <>
            <p>
              Die Website wird über Firebase Hosting von Google (Google Ireland Limited, Gordon House,
              Barrow Street, Dublin 4, Irland, bzw. Google LLC, USA) als Auftragsverarbeiter
              bereitgestellt. Beim Aufruf jeder Seite verarbeitet Google technisch notwendige Daten:
              IP-Adresse, Datum und Uhrzeit, aufgerufene Adresse, Browser- und Geräteangaben sowie die
              zuvor besuchte Seite. Das ist erforderlich, um die Website auszuliefern und vor Missbrauch
              zu schützen.
            </p>
            <p>
              Rechtsgrundlage ist unser berechtigtes Interesse an einem sicheren und funktionierenden
              Webauftritt (Art. 6 Abs. 1 lit. f DSGVO). Wir werten diese Protokolle nicht aus und
              führen sie nicht mit anderen Daten zusammen. Soweit Daten in die USA übermittelt werden,
              stützt sich das auf den Angemessenheitsbeschluss zum EU-US Data Privacy Framework, unter
              dem Google LLC zertifiziert ist. Datenschutzhinweise von Google: {ext(GOOGLE_PRIVACY)}
            </p>
          </>
        ),
      },
      {
        heading: 'Speicherung in Ihrem Browser',
        body: (
          <>
            <p>
              Wir setzen keine Cookies. Einige Einstellungen speichert die Website im lokalen Speicher
              Ihres Browsers (localStorage / sessionStorage). Diese Daten verlassen Ihr Gerät nicht und
              werden nicht an uns übertragen, mit Ausnahme der Antworten einer Umfrage, die Sie am Ende
              selbst absenden:
            </p>
            <ul>
              <li>
                <code>softmaze.theme</code> — das gewählte Farbschema (hell, dunkel, automatisch), bis Sie
                es ändern oder die Browserdaten löschen.
              </li>
              <li>
                <code>softmaze.notice</code> — dass Sie den Hinweis zur Speicherung geschlossen haben,
                für ein Jahr.
              </li>
              <li>
                <code>softmaze.announcement</code> — dass Sie die Ankündigung (derzeit zum NISG 2026)
                geschlossen haben, für 30 Tage.
              </li>
              <li>
                Sandbox-Demos (<code>&lt;projekt&gt;.sandbox.*</code>) — Ihre Sitzung und Ihre Änderungen
                an den Beispieldaten. Sie werden verworfen, wenn Sie die Sandbox beenden oder den Tab
                schließen.
              </li>
              <li>
                Umfragen und Nutzertests — Ihre Antworten, solange Sie teilnehmen. Nach dem erfolgreichen
                Absenden werden sie aus Ihrem Browser gelöscht; schlägt das Absenden fehl, bleiben sie, damit
                Sie es erneut versuchen können.
              </li>
            </ul>
            <p>
              Diese Speicherung ist für die von Ihnen ausdrücklich gewünschten Funktionen unbedingt
              erforderlich (§ 165 Abs. 3 TKG 2021) und braucht daher keine Einwilligung. Sie können
              die Daten jederzeit über die Einstellungen Ihres Browsers löschen.
            </p>
          </>
        ),
      },
      {
        heading: 'Google Maps',
        body: (
          <>
            <p>
              Im Kontaktbereich können Sie eine Karte von Google Maps (Google Ireland Limited) anzeigen.
              Die Karte wird erst geladen, wenn Sie auf „Karte laden“ klicken. Erst dann stellt Ihr
              Browser eine Verbindung zu Google her; dabei werden insbesondere Ihre IP-Adresse, die
              aufgerufene Seite und Browserangaben an Google übermittelt, und Google kann Cookies auf
              Ihrem Gerät speichern oder auslesen.
            </p>
            <p>
              Rechtsgrundlage ist Ihre Einwilligung durch den Klick (Art. 6 Abs. 1 lit. a DSGVO,
              § 165 Abs. 3 TKG 2021). Sie gilt nur für den aktuellen Seitenaufruf; nach dem Neuladen
              wird die Karte nicht mehr automatisch angezeigt. Zur Übermittlung in die USA gilt das
              oben zum Data Privacy Framework Gesagte. Datenschutzhinweise von Google:{' '}
              {ext(GOOGLE_PRIVACY)}
            </p>
          </>
        ),
      },
      {
        heading: 'Kontakt per E-Mail, Telefon oder WhatsApp',
        body: (
          <>
            <p>
              Wenn Sie uns kontaktieren, verarbeiten wir die Angaben, die Sie uns schicken (etwa Name,
              E-Mail-Adresse oder Telefonnummer und den Inhalt Ihrer Nachricht), um Ihre Anfrage zu
              beantworten. Rechtsgrundlage ist die Anbahnung oder Erfüllung eines Vertrags
              (Art. 6 Abs. 1 lit. b DSGVO) bzw. unser berechtigtes Interesse an der Beantwortung
              (Art. 6 Abs. 1 lit. f DSGVO). Unser E-Mail-Postfach wird von Google betrieben.
            </p>
            <p>
              Wir speichern Ihre Anfrage, solange sie bearbeitet wird. Entsteht daraus ein Auftrag,
              gelten die gesetzlichen Aufbewahrungsfristen (in der Regel sieben Jahre, § 132 BAO).
            </p>
            <p>
              Wenn Sie WhatsApp (Meta) nutzen, verarbeitet auch dieser Anbieter Ihre Daten nach seinen
              eigenen Datenschutzbestimmungen, teils außerhalb der EU. Für vertrauliche
              Inhalte empfehlen wir E-Mail.
            </p>
          </>
        ),
      },
      ...(CONTACT_FORM_ENABLED
        ? [
            {
              heading: 'Kontaktformular',
              body: (
                <p>
                  Beim Absenden sendet Ihr Browser die Angaben aus dem Kontaktformular (Name,
                  E-Mail-Adresse, Betreff, Nachricht) an den Versanddienst EmailJS (EmailJS Pte. Ltd.,
                  Singapur; Server bei Amazon Web Services in den USA). EmailJS leitet sie als E-Mail
                  an unser Postfach weiter; dabei erhält EmailJS auch Ihre IP-Adresse. Die
                  Übermittlung in Drittländer stützt sich auf Standardvertragsklauseln.
                  Rechtsgrundlagen und Speicherdauer wie oben unter „Kontakt“. Datenschutzhinweise
                  von EmailJS: {ext(EMAILJS_PRIVACY)}
                </p>
              ),
            },
          ]
        : []),
      {
        heading: 'Sandbox-Demos',
        body: (
          <>
            <p>
              Die Sandbox-Demos unter /demo/… laufen vollständig in Ihrem Browser mit Beispieldaten.
              Ihre Eingaben werden nicht an uns oder andere Server gesendet.
            </p>
          </>
        ),
      },
      {
        heading: 'Umfragen und Nutzertests',
        body: (
          <>
            <p>
              Von Zeit zu Zeit bitten wir um die Teilnahme an Umfragen oder Nutzertests, etwa um
              Prototypen von Apps auszuprobieren. Laufende und abgeschlossene Studien finden Sie im{' '}
              {studies('Archiv')}. Zu Beginn jeder Studie erklären wir, was genau erfasst wird, und bitten
              um Ihre Einwilligung. Ohne Einwilligung startet die Studie nicht; Nachteile haben Sie
              dadurch keine.
            </p>
            <p>
              Erfasst werden je nach Studie: Ihre Antworten einschließlich freiwilliger Kommentare,
              allgemeine Angaben wie Altersgruppe, wie Sie einen Prototyp bedienen (aufgerufene Ansichten,
              Tipp- und Klickstellen, Dauer), Bildschirmgröße, ob Ihr Gerät einen Touchscreen hat,
              Sprache und Zeitpunkt. Wir fragen nicht nach Name, E-Mail-Adresse oder anderen Angaben, die
              Sie direkt erkennbar machen; bitte machen Sie solche Angaben auch nicht in Kommentarfeldern.
              Jede Teilnahme erhält einen zufälligen Code.
            </p>
            <p>
              Zweck ist ausschließlich, unsere Produkte und die Produkte, die wir für Kunden entwickeln,
              leichter bedienbar zu machen. Auftraggebern geben wir nur zusammengefasste Ergebnisse ohne
              Teilnahme-Codes weiter. Rechtsgrundlage ist Ihre Einwilligung (Art. 6 Abs. 1 lit. a DSGVO);
              teilnehmen können Personen ab 14 Jahren (§ 4 Abs. 4 DSG).
            </p>
            <p>
              Während Sie teilnehmen, liegen Ihre Antworten nur im Speicher Ihres Browsers. Erst wenn Sie
              die Studie abschließen, werden sie in eine Tabelle in unserem Google-Workspace-Konto
              übertragen (Google Sheets und Google Apps Script; Google Ireland Limited als
              Auftragsverarbeiter, zur Übermittlung in die USA siehe „Hosting“). Wir löschen die Antworten
              spätestens 12 Monate nach Ende der jeweiligen Studie; zusammengefasste Auswertungen ohne
              Personenbezug können wir länger aufbewahren.
            </p>
            <p>
              Bis zum Abschluss brechen Sie ab, indem Sie die Seite schließen; dann wird nichts
              übertragen. Danach können Sie Ihre Einwilligung widerrufen, indem Sie uns den Code schicken,
              der Ihnen am Ende angezeigt wird; wir löschen dann Ihre Antworten. Ohne diesen Code können
              wir Ihre Antworten nicht zuordnen (Art. 11 DSGVO).
            </p>
          </>
        ),
      },
      {
        heading: 'Ihre Rechte',
        body: (
          <>
            <p>
              Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung,
              Datenübertragbarkeit und Widerspruch (Art. 15–21 DSGVO) sowie das Recht, eine Einwilligung
              jederzeit mit Wirkung für die Zukunft zu widerrufen. Schreiben Sie uns dazu an{' '}
              <a href="mailto:info@softmaze.at">info@softmaze.at</a>.
            </p>
            <p>
              Sie können sich außerdem bei der Österreichischen Datenschutzbehörde beschweren:
              Barichgasse 40–42, 1030 Wien, <a href="mailto:dsb@dsb.gv.at">dsb@dsb.gv.at</a>,{' '}
              {ext('https://www.dsb.gv.at')}.
            </p>
          </>
        ),
      },
    ],
  }

  const en: typeof de = {
    eyebrow: 'Legal',
    title: 'Privacy Policy',
    updated: 'Last updated: November 2026',
    sections: [
      {
        heading: 'Controller',
        body: (
          <>
            <p>
              SoftMaze Solutions GmbH, Gablenzgasse 21/GL/2-4, 1150 Vienna, Austria
              <br />
              Email: <a href="mailto:info@softmaze.at">info@softmaze.at</a> · Phone: +43 660 5369991
            </p>
            <p>More company details are in the {imprint('imprint')}.</p>
          </>
        ),
      },
      {
        heading: 'In short',
        body: (
          <ul>
            <li>No tracking, no analytics, no advertising, no social media plugins.</li>
            <li>We set no cookies.</li>
            <li>Google Maps loads only after you click “Load map”.</li>
            <li>You take part in surveys and user tests only with your consent.</li>
            <li>Fonts and every other file of the site come from our own hosting.</li>
          </ul>
        ),
      },
      {
        heading: 'Hosting and server logs',
        body: (
          <>
            <p>
              The site is served through Firebase Hosting by Google (Google Ireland Limited, Gordon
              House, Barrow Street, Dublin 4, Ireland, or Google LLC, USA) as our processor. For every
              page request Google processes technically necessary data: IP address, date and time, the
              requested address, browser and device details and the referring page. This is needed to
              deliver the site and protect it from abuse.
            </p>
            <p>
              The legal basis is our legitimate interest in a secure, working website (Art. 6(1)(f)
              GDPR). We do not analyse these logs or combine them with other data. Where data is
              transferred to the USA, this relies on the adequacy decision for the EU-US Data Privacy
              Framework, under which Google LLC is certified. Google’s privacy policy:{' '}
              {ext(GOOGLE_PRIVACY)}
            </p>
          </>
        ),
      },
      {
        heading: 'Storage in your browser',
        body: (
          <>
            <p>
              We set no cookies. The site keeps a few settings in your browser’s local storage
              (localStorage / sessionStorage). This data stays on your device and is not sent to us, except
              survey answers that you send yourself at the end:
            </p>
            <ul>
              <li>
                <code>softmaze.theme</code> — your colour scheme (light, dark, auto), until you change it
                or clear your browser data.
              </li>
              <li>
                <code>softmaze.notice</code> — that you closed the storage notice, for one year.
              </li>
              <li>
                <code>softmaze.announcement</code> — that you closed the announcement (currently about
                the NISG 2026), for 30 days.
              </li>
              <li>
                Sandbox demos (<code>&lt;project&gt;.sandbox.*</code>) — your session and your changes to
                the sample data, discarded when you exit the sandbox or close the tab.
              </li>
              <li>
                Surveys and user tests — your answers while you take part. They are deleted from your
                browser once they have been sent; if sending fails, they stay so that you can try again.
              </li>
            </ul>
            <p>
              This storage is strictly necessary for features you explicitly ask for (§ 165(3) TKG
              2021, Austrian Telecommunications Act) and so needs no consent. You can delete it at any
              time in your browser settings.
            </p>
          </>
        ),
      },
      {
        heading: 'Google Maps',
        body: (
          <>
            <p>
              In the contact section you can show a Google Maps map (Google Ireland Limited). It loads
              only after you click “Load map”. Only then does your browser connect to Google, sending
              in particular your IP address, the page you are on and browser details; Google may also
              store or read cookies on your device.
            </p>
            <p>
              The legal basis is the consent you give by clicking (Art. 6(1)(a) GDPR, § 165(3) TKG
              2021). It applies to the current page view only; after a reload the map is not shown
              automatically. Transfers to the USA are covered as described above. Google’s privacy
              policy: {ext(GOOGLE_PRIVACY)}
            </p>
          </>
        ),
      },
      {
        heading: 'Contact by email, phone or WhatsApp',
        body: (
          <>
            <p>
              When you contact us, we process what you send us (such as your name, email address or
              phone number and your message) to answer your enquiry. The legal basis is taking steps
              towards or performing a contract (Art. 6(1)(b) GDPR) or our legitimate interest in
              replying (Art. 6(1)(f) GDPR). Our mailbox is operated by Google.
            </p>
            <p>
              We keep your enquiry while it is being handled. If it leads to an engagement, statutory
              retention periods apply (usually seven years, § 132 BAO, Austrian Federal Fiscal Code).
            </p>
            <p>
              If you use WhatsApp (Meta), Meta also processes your data under its own privacy policy,
              partly outside the EU. For confidential matters we recommend
              email.
            </p>
          </>
        ),
      },
      ...(CONTACT_FORM_ENABLED
        ? [
            {
              heading: 'Contact form',
              body: (
                <p>
                  When you send the contact form, your browser passes what you entered (name, email
                  address, subject, message) to the email service EmailJS (EmailJS Pte. Ltd.,
                  Singapore; servers at Amazon Web Services in the USA), which forwards it as an email
                  to our mailbox; EmailJS also receives your IP address. Transfers outside the EU rely
                  on Standard Contractual Clauses. Legal bases and retention as under “Contact” above.
                  EmailJS’s privacy policy: {ext(EMAILJS_PRIVACY)}
                </p>
              ),
            },
          ]
        : []),
      {
        heading: 'Sandbox demos',
        body: (
          <>
            <p>
              The sandbox demos under /demo/… run entirely in your browser on sample data. What you
              enter is not sent to us or any other server.
            </p>
          </>
        ),
      },
      {
        heading: 'Surveys and user tests',
        body: (
          <>
            <p>
              From time to time we invite people to take part in surveys or user tests, for example to try
              out app prototypes. Current and past studies are listed in our {studies('archive')}. At the
              start of each study we explain exactly what is recorded and ask for your consent. Without it
              the study does not start, and declining has no consequences for you.
            </p>
            <p>
              Depending on the study we record: your answers including optional comments, general details
              such as your age group, how you use a prototype (screens viewed, where you tap or click, how
              long it takes), your screen size, whether your device has a touchscreen, language and time.
              We do not ask for your name, email address or anything else that identifies you directly;
              please do not enter such details in comment fields either. Each participation gets a random
              code.
            </p>
            <p>
              The only purpose is to make our products, and the products we build for clients, easier to
              use. Clients receive only aggregated results without participation codes. The legal basis
              is your consent (Art. 6(1)(a) GDPR); you must be at least 14 to take part (§ 4(4) DSG,
              Austrian Data Protection Act).
            </p>
            <p>
              While you take part, your answers are kept only in your browser’s storage. Only when you
              finish are they sent to a spreadsheet in our Google Workspace account (Google Sheets and
              Google Apps Script; Google Ireland Limited as our processor; for transfers to the USA see
              “Hosting”). We delete the answers at the latest 12 months after the study ends; we may keep
              aggregated, non-personal results longer.
            </p>
            <p>
              Until you finish, you can stop by closing the page and nothing is sent. Afterwards you can
              withdraw your consent by sending us the code shown at the end, and we will delete your
              answers. Without that code we cannot tell which answers are yours (Art. 11 GDPR).
            </p>
          </>
        ),
      },
      {
        heading: 'Your rights',
        body: (
          <>
            <p>
              You have the right of access, rectification, erasure, restriction of processing, data
              portability and objection (Art. 15–21 GDPR), and you can withdraw any consent at any time
              with effect for the future. Write to us at{' '}
              <a href="mailto:info@softmaze.at">info@softmaze.at</a>.
            </p>
            <p>
              You can also lodge a complaint with the Austrian Data Protection Authority
              (Datenschutzbehörde): Barichgasse 40–42, 1030 Vienna,{' '}
              <a href="mailto:dsb@dsb.gv.at">dsb@dsb.gv.at</a>, {ext('https://www.dsb.gv.at')}.
            </p>
          </>
        ),
      },
    ],
  }

  return { de, en }
}

export default function PrivacyPage() {
  const { lang, to } = useI18n()
  const locale = toLocale(lang)
  const c = copy(to)[locale]
  useDocumentMeta({ ...PAGES['/privacy'][locale], path: '/privacy', locale })
  return (
    <ThemeProvider>
      <Header active="home" onNavigate={() => {}} />

      <main className="imprint-page">
        <div className="container imprint-content">
          <span className="eyebrow">{c.eyebrow}</span>
          <h1 className="imprint-title">{c.title}</h1>

          {c.sections.map((s) => (
            <section key={s.heading} className="imprint-section">
              <h2>{s.heading}</h2>
              {s.body}
            </section>
          ))}

          <p className="mono muted">{c.updated}</p>
        </div>
      </main>

      <Footer />
    </ThemeProvider>
  )
}
